"use client";

import { ChevronDown, X } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export interface MultiSelectOption {
  /** Stable identity, and what `value` / `onChange` carry. */
  value: string;
  /** Main label of the row, and of the chip unless `chipLabel` says otherwise. */
  label: string;
  /**
   * Shorter label for the chip, when the row label is too long to sit on the
   * trigger (a country name against its ISO code, say).
   */
  chipLabel?: string;
  /** Optional short label shown before `label` (a country code, a rank…). */
  prefix?: React.ReactNode;
  /** Optional right-aligned detail (a served zone, a count…). */
  meta?: React.ReactNode;
  /** Text the search box matches, on top of `label` and `value`. */
  keywords?: string;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: React.ReactNode;
  /** Shown in place of the list when `options` is empty. */
  emptyMessage?: React.ReactNode;
  /** Shown when a search filters everything out. Enables the search box. */
  noResultsMessage?: React.ReactNode;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  contentClassName?: string;
  /**
   * Label of a selected value whose option is gone. Selections can outlive
   * their options (a rule pointing at a deleted product), and dropping them
   * silently would make the chip disappear while the data is still there.
   */
  renderMissingLabel?: (value: string) => React.ReactNode;
  /**
   * Inside a Dialog: the portalled list sits outside the dialog's scroll lock,
   * which swallows the wheel unless the popover takes the lock itself.
   */
  modal?: boolean;
  /**
   * An × on each chip. Turn it off when the list the trigger opens is enough
   * to untick values: the chips then read as plain labels.
   */
  hasRemovableChips?: boolean;
}

/**
 * Pick several values from a list, with removable chips on the trigger.
 *
 * Extracted from the two hand-rolled copies in `track-office` (countries, then
 * carrier products); everything that differed between them is a prop.
 */
export function MultiSelect({
  options,
  value,
  onChange,
  placeholder,
  emptyMessage,
  noResultsMessage,
  searchPlaceholder,
  disabled = false,
  className,
  contentClassName,
  renderMissingLabel,
  modal,
  hasRemovableChips = true,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  /**
   * What the user just clicked, until the parent answers with it. Without it a
   * controlled `value` that only changes once a save round-trips shows nothing
   * happening, and the next toggle drops the previous one. Cleared when `value`
   * changes or when the parent releases the control, so a rejected save falls
   * back to the truth.
   */
  const [optimistic, setOptimistic] = React.useState<string[] | null>(null);
  const selected = optimistic ?? value;

  // Keyed on the content, not on the array: a parent hands down a new one on
  // every render, so an effect watching `value` would clear the optimistic
  // state before it was ever shown.
  const valueKey = value.join("\u0000");
  React.useEffect(() => setOptimistic(null), [valueKey]);

  const wasDisabled = React.useRef(disabled);
  React.useEffect(() => {
    if (wasDisabled.current && !disabled) setOptimistic(null);
    wasDisabled.current = disabled;
  }, [disabled]);

  const isSearchable = noResultsMessage !== undefined;

  const filtered = React.useMemo(() => {
    if (!isSearchable || !search.trim()) return options;
    const needle = search.toLowerCase();
    return options.filter((option) =>
      [option.label, option.value, option.keywords ?? ""].some((haystack) =>
        haystack.toLowerCase().includes(needle),
      ),
    );
  }, [options, search, isSearchable]);

  const apply = (next: string[]) => {
    setOptimistic(next);
    onChange(next);
  };

  const toggle = (optionValue: string) => {
    apply(
      selected.includes(optionValue)
        ? selected.filter((v) => v !== optionValue)
        : [...selected, optionValue],
    );
  };

  const remove = (optionValue: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled) return;
    apply(selected.filter((v) => v !== optionValue));
  };

  const labelOf = (optionValue: string) => {
    const option = options.find((o) => o.value === optionValue);
    if (option) return option.chipLabel ?? option.label;
    return renderMissingLabel ? renderMissingLabel(optionValue) : optionValue;
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal h-auto min-h-9 py-1.5",
            !selected.length && "text-muted-foreground",
            className,
          )}
        >
          {/* `min-w-0` is what actually clips. A flex child defaults to its
              content width, so `truncate` alone lets a long label push straight
              through the button's border. */}
          <div className="flex flex-wrap gap-1 flex-1 min-w-0 text-left">
            {selected.length === 0 ? (
              <span className="truncate">{placeholder}</span>
            ) : (
              selected.map((optionValue) => (
                <span
                  key={optionValue}
                  className="inline-flex items-center gap-1 bg-muted px-1.5 py-0.5 rounded text-xs font-medium max-w-full"
                >
                  <span className="truncate">{labelOf(optionValue)}</span>
                  {hasRemovableChips && (
                    <X
                      className={cn(
                        "h-3 w-3 shrink-0",
                        disabled
                          ? "opacity-50"
                          : "cursor-pointer hover:text-destructive",
                      )}
                      onClick={(event) => remove(optionValue, event)}
                    />
                  )}
                </span>
              ))
            )}
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-[280px] p-0", contentClassName)}
        align="start"
      >
        {isSearchable && (
          <div className="p-2 border-b">
            <Input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-8"
            />
          </div>
        )}
        <div className="max-h-[240px] overflow-y-auto p-2">
          {options.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-2 px-1">
              {emptyMessage}
            </p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-2 px-1">
              {noResultsMessage}
            </p>
          ) : (
            <div className="space-y-1">
              {filtered.map((option) => (
                <label
                  key={option.value}
                  className={cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded text-sm",
                    disabled
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-muted cursor-pointer",
                  )}
                >
                  {/* Disabled with the parent, not just the trigger: a popover
                      left open over a disabled trigger keeps taking clicks. */}
                  <Checkbox
                    checked={selected.includes(option.value)}
                    disabled={disabled}
                    onCheckedChange={() => toggle(option.value)}
                  />
                  {option.prefix !== undefined && (
                    <span className="font-medium text-muted-foreground w-8 shrink-0">
                      {option.prefix}
                    </span>
                  )}
                  <span className="flex-1 min-w-0">{option.label}</span>
                  {option.meta !== undefined && (
                    // Capped: an unbounded `shrink-0` lets a long meta push the
                    // label off the row.
                    <span className="max-w-[45%] shrink-0 truncate text-xs text-muted-foreground">
                      {option.meta}
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
